import { helpers, required } from '@vuelidate/validators'
import { EPHONE_CODES, EPHONE_PREFIXES, type PhoneNumberCountry } from './usePhone.types'
import type { TInstance } from '@/components/Inputs/Composables/validators/validators.types'

const PHONE_NUMBER_REGEX: Record<EPHONE_CODES, RegExp> = {
  [EPHONE_CODES.POLAND]: /^[1-9]\d{8}$/, // 9 digits, prefix selected separately
  [EPHONE_CODES.TURKEY]: /^[1-9]\d{9}$/, // 10 digits
  [EPHONE_CODES.BULGARIA]: /^[1-9]\d{7,8}$/, // 8-9 digits
  [EPHONE_CODES.CROATIA]: /^[1-9]\d{7,8}$/, // 8-9 digits
  [EPHONE_CODES.CZECH_REPUBLIC]: /^[1-9]\d{8}$/, // 9 digits
  [EPHONE_CODES.SLOVAKIA]: /^[1-9]\d{8}$/, // 9 digits
  [EPHONE_CODES.OTHER]: /^\+\d{8,14}$/, // + and 8-14 digits
}

const PHONE_NUMBER_REGEX_WITH_PREFIX: Record<EPHONE_CODES, RegExp> = {
  [EPHONE_CODES.POLAND]: /^\+48[1-9]\d{8}$/, // +48 and 9 digits
  [EPHONE_CODES.TURKEY]: /^\+90[1-9]\d{9}$/, // +90 and 10 digits
  [EPHONE_CODES.BULGARIA]: /^\+359[1-9]\d{7,8}$/, // +359 and 8-9 digits
  [EPHONE_CODES.CROATIA]: /^\+385[1-9]\d{7,8}$/, // +385 and 8-9 digits
  [EPHONE_CODES.CZECH_REPUBLIC]: /^\+420[1-9]\d{8}$/, // +420 and 9 digits
  [EPHONE_CODES.SLOVAKIA]: /^\+421[1-9]\d{8}$/, // +421 and 9 digits
  [EPHONE_CODES.OTHER]: /^\+\d{8,14}$/, // + and 8-14 digits
}

const PHONE_PREFIX_TO_COUNTRY: Record<string, string> = {
  [`${EPHONE_PREFIXES.POLAND}`]: EPHONE_CODES.POLAND,
  [`${EPHONE_PREFIXES.TURKEY}`]: EPHONE_CODES.TURKEY,
  [`${EPHONE_PREFIXES.BULGARIA}`]: EPHONE_CODES.BULGARIA,
  [`${EPHONE_PREFIXES.CROATIA}`]: EPHONE_CODES.CROATIA,
  [`${EPHONE_PREFIXES.CZECH_REPUBLIC}`]: EPHONE_CODES.CZECH_REPUBLIC,
  [`${EPHONE_PREFIXES.SLOVAKIA}`]: EPHONE_CODES.SLOVAKIA,
}

export const mapPrefixToCountry = (prefix: string) => {
  return PHONE_PREFIX_TO_COUNTRY[prefix]
}

export interface UsePhoneOptions {
  prefixRequired?: boolean
}

export function usePhone(
  field: string,
  country: PhoneNumberCountry | Ref<PhoneNumberCountry> | ComputedRef<string | undefined>,
  t: TInstance,
  message?: () => string,
  options: UsePhoneOptions = {},
) {
  const getCountryCode = (): string | undefined => isRef(country) ? country.value : country

  const phoneNumberValidator = helpers.withMessage(
    message ?? (() => t(`formValidatorMessages.phoneNumber.${getCountryCode() ?? 'default'}`)),
    (value: string) => {
      const countryCode = getCountryCode()
      if (!value || !countryCode) {
        return true
      }
      const regexMap = options.prefixRequired ? PHONE_NUMBER_REGEX_WITH_PREFIX : PHONE_NUMBER_REGEX
      const regex = regexMap[countryCode as keyof typeof regexMap]
      if (!regex) return true
      return regex.test(value)
    })

  return ({
    [`${field}`]: {
      required: helpers.withMessage(
        () => t('formValidatorMessages.phoneNumber.required'),
        required,
      ),
      phoneNumberValidator,
    },
  })
}
